import {
    getWmsPushPlan,
    markShipmentReceived,
    markStandaloneShipmentReceived,
    pushShipmentToWms,
    pushStandaloneShipmentToWms,
    unmarkShipmentReceived,
    updateShipmentMilestones,
    WMS_STEP_LABEL,
    type WmsPushPlan,
} from '@api/mother-order';
import type { Shipment } from '@api/shipments';
import { getVendorLanes } from '@api/vendors';
import { destinationWarehouses } from '../orders/inbound';
import { ShipmentPaymentsBlock } from '../orders/PaymentScheduleSection';
import { DispatchBlock } from './DispatchBlock';
import { listWarehousesForStn } from '@api/warehouse-ops';
import { DataSheet } from '@components/ui/DataSheet';
import { DateSinglePicker } from '@components/ui/DateRange';
import SegmentedToggle from '@components/ui/SegmentedToggle';
import useToaster from '@hooks/toaster/useToaster';
import { useCbmEnabled } from '@hooks/useCbmEnabled';
import { Select } from '@mantine/core';
import { X } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useState } from 'react';

import {
    formatDateDisplay,
    PanelField,
    panelSectionLabel,
    StatusBadge,
    textColors,
} from '../procurementOrders/shared';
import { formatCount } from '@utils/currency';

const num = (n: number): string => formatCount(n || 0);

const GREEN = 'rgb(45, 187, 136)';
const AMBER = 'rgb(202, 138, 4)';
const RED = 'rgb(205, 101, 101)';

const startOfDay = (d: string | Date): Date => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};
const dayDiff = (from: string | Date, to: string | Date): number =>
    Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86400000);
const plural = (n: number): string => (n === 1 ? '' : 's');

// Variance of an actual vs expected milestone date (or, if not yet reached,
// how it tracks against today). Returns chip text + colour, and whether the
// node is "done" (has an actual).
function variance(
    expected: string | null | undefined,
    actual: string | null | undefined,
): { text: string; color: string; done: boolean } {
    if (actual) {
        if (!expected) return { text: '', color: GREEN, done: true };
        const d = dayDiff(expected, actual); // actual − expected
        if (d <= 0)
            return {
                text: d === 0 ? 'on time' : `${-d} day${plural(-d)} early`,
                color: GREEN,
                done: true,
            };
        return { text: `${d} day${plural(d)} late`, color: AMBER, done: true };
    }
    if (!expected) return { text: '', color: 'rgb(148, 163, 184)', done: false };
    const d = dayDiff(new Date(), expected); // expected − today
    if (d >= 0)
        return {
            text: d === 0 ? 'due today' : `due in ${d} day${plural(d)}`,
            color: 'rgb(148, 163, 184)',
            done: false,
        };
    return { text: `${-d} day${plural(-d)} overdue`, color: RED, done: false };
}

type MilestoneField =
    | 'readinessDate'
    | 'departureDate'
    | 'arrivalDate'
    | 'deliveryDate';
type ItemRow = { name: string; sku: string; quantity: number; received: number };
type TimelineRow = {
    label: string;
    field: MilestoneField;
    /** Lane-derived. */
    expected: string | null | undefined;
    /** The plan: typed ahead, moved as it slips. */
    plan: string | null | undefined;
    /** What happened: a stamp a person or a sync wrote. Readiness has none (it is the vendor's claim). */
    actual: string | null | undefined;
    /** Readiness counts once its date is reached; the rest only once stamped. */
    reachedCounts: boolean;
};

/**
 * Focused, read-only side panel for a single shipment — vendor, quantity,
 * milestone dates, received, and the line items. Intentionally NOT the mother
 * order detail (that lives in the Orders tab); this avoids repetition and keeps
 * the Shipments tab about the consignment itself.
 */
const ShipmentDetailPanel: FC<{
    shipment: Shipment | null;
    onClose: () => void;
    onChanged: () => void;
}> = ({ shipment, onClose, onChanged }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const cbmEnabled = useCbmEnabled();
    const [savingWarehouse, setSavingWarehouse] = useState(false);
    const [savingReceive, setSavingReceive] = useState(false);
    const [savingWmsPush, setSavingWmsPush] = useState(false);
    // Receiving is confirmed in a strip with the WMS option, when the merchant has one.
    const [confirmReceive, setConfirmReceive] = useState(false);
    const [receivePushToWms, setReceivePushToWms] = useState(true);
    const NO_WMS_PLAN: WmsPushPlan = { wmsType: null, mode: null, order: null, shipment: null, receive: null, standaloneShipment: null, standaloneReceive: null, stn: null, distribution: null };
    const { data: wmsPlanData } = useQuery(['wms-push-plan'], getWmsPushPlan, { staleTime: 5 * 60_000 });
    const wmsPlan = wmsPlanData ?? NO_WMS_PLAN;
    const [paneView, setPaneView] = useState<'timeline' | 'payments' | 'items'>(
        'timeline',
    );
    // A different shipment opens back on the first tab.
    const shipmentKey = shipment?.poNumber ?? null;
    useEffect(() => {
        setPaneView('timeline');
        setConfirmReceive(false);
        setReceivePushToWms(true);
    }, [shipmentKey]);
    const { data: warehouses } = useQuery(['warehouses-for-shipment'], () =>
        listWarehousesForStn(),
    );
    // Only the warehouses this vendor has a lane to are destinations: the lane is what
    // gives the shipment its expected dates.
    const vendorId = shipment?.vendorId ?? null;
    const { data: vendorLanes } = useQuery(
        ['vendor-lanes', vendorId],
        () => getVendorLanes(vendorId as string),
        { enabled: !!vendorId },
    );
    const currentWarehouseId = shipment?.warehouseId ?? null;
    const { options: destinationOptions, laneScoped } = useMemo(
        () => destinationWarehouses(warehouses ?? [], vendorLanes, currentWarehouseId),
        [warehouses, vendorLanes, currentWarehouseId],
    );
    const noLanesYet = !!vendorId && vendorLanes !== undefined && !laneScoped;

    const setWarehouse = async (value: string | null) => {
        if (!shipment?.motherOrderId) return;
        setSavingWarehouse(true);
        try {
            await updateShipmentMilestones(shipment.motherOrderId, shipment.poNumber, {
                warehouseId: value,
            });
            showToastSuccess({
                title: value ? 'Destination warehouse set' : 'Warehouse cleared',
            });
            onChanged();
        } catch (e: any) {
            showToastError({
                title: 'Could not update warehouse',
                subtitle: e?.error?.message || e?.message || 'Please retry.',
            });
        } finally {
            setSavingWarehouse(false);
        }
    };

    const setMilestone = async (field: MilestoneField, iso: string | null) => {
        if (!shipment?.motherOrderId) return;
        try {
            await updateShipmentMilestones(shipment.motherOrderId, shipment.poNumber, {
                [field]: iso,
            });
            showToastSuccess({ title: iso ? 'Date updated' : 'Date cleared' });
            onChanged();
        } catch (e: any) {
            showToastError({
                title: 'Could not update date',
                subtitle: e?.error?.message || e?.message || 'Please retry.',
            });
        }
    };

    const markReceived = async (pushToWms: boolean) => {
        if (!shipment) return;
        setSavingReceive(true);
        try {
            // A production shipment has no mother order; it is received on its own row, and
            // its WMS receipt is always a GRN against its own WMS PO.
            const receiveStep = shipment.motherOrderId ? wmsPlan.receive : wmsPlan.standaloneReceive;
            if (shipment.motherOrderId) {
                await markShipmentReceived(shipment.motherOrderId, shipment.poNumber, { pushToWms });
            } else {
                await markStandaloneShipmentReceived(shipment.poNumber, { pushToWms });
            }
            showToastSuccess({
                title: pushToWms && receiveStep
                    ? `Shipment marked received · ${receiveStep === 'COMPLETE_GRN' ? 'GRN completed' : 'GRN created'} in WMS`
                    : 'Shipment marked received',
            });
            setConfirmReceive(false);
            onChanged();
        } catch (e: any) {
            // The WMS step runs before our own mark, so a failure here means NOTHING changed.
            showToastError({
                title: pushToWms ? 'WMS refused the receipt — shipment not marked' : 'Could not mark received',
                subtitle: e?.error?.message || e?.message || 'Please retry.',
            });
        } finally {
            setSavingReceive(false);
        }
    };

    const pushToWms = async () => {
        if (!shipment) return;
        const step = shipment.motherOrderId ? wmsPlan.shipment : wmsPlan.standaloneShipment;
        if (!step) return;
        setSavingWmsPush(true);
        try {
            if (shipment.motherOrderId) {
                await pushShipmentToWms(shipment.motherOrderId, shipment.poNumber);
            } else {
                await pushStandaloneShipmentToWms(shipment.poNumber);
            }
            showToastSuccess({
                title: step === 'CREATE_PO' ? 'Purchase order created in WMS' : 'GRN created in WMS',
            });
            onChanged();
        } catch (e: any) {
            showToastError({
                title: 'WMS push failed',
                subtitle: e?.error?.message || e?.message || 'Please retry.',
            });
        } finally {
            setSavingWmsPush(false);
        }
    };

    const unmarkReceived = async () => {
        if (!shipment?.motherOrderId) return;
        setSavingReceive(true);
        try {
            await unmarkShipmentReceived(shipment.motherOrderId, shipment.poNumber);
            showToastSuccess({ title: 'Manual receive reversed' });
            onChanged();
        } catch (e: any) {
            showToastError({
                title: 'Could not reverse receive',
                subtitle: e?.error?.message || e?.message || 'Please retry.',
            });
        } finally {
            setSavingReceive(false);
        }
    };

    if (!shipment) return null;

    // Standalone shipments (no mother): a production dispatch, or a synced PO. Milestones
    // still flow from the daily sync, but the WMS push and the receipt are live for them —
    // a production shipment always takes the per-shipment path (its own WMS PO, then a GRN).
    const isStandalone = !shipment.motherOrderId;
    const manuallyReceived = !!shipment.manualReceivedAt;
    const shipmentStep = isStandalone ? wmsPlan.standaloneShipment : wmsPlan.shipment;
    const receiveStep = isStandalone ? wmsPlan.standaloneReceive : wmsPlan.receive;
    // Under PO_PER_SHIPMENT the shipment's own PO is the push; under PO_AT_ORDER its GRN is.
    const wmsPushed = shipmentStep === 'CREATE_PO' ? !!shipment.wmsPoCode : !!shipment.wmsGrnCode;
    const canPushToWms = !!shipmentStep && !wmsPushed && !manuallyReceived;
    // Under PO_AT_ORDER every WMS document for a mother-order shipment hangs off the order's WMS PO.
    const orderNotPushed = !isStandalone && wmsPlan.mode === 'PO_AT_ORDER' && !shipment.motherWmsPoCode;
    const pushHint = orderNotPushed
        ? 'Push the order to the WMS first — a GRN needs the WMS purchase order to receive against.'
        : shipmentStep
            ? `Will ${WMS_STEP_LABEL[shipmentStep]}${shipment.motherWmsPoCode ? ` against PO ${shipment.motherWmsPoCode}` : ''}`
            : undefined;
    const wmsSummary = [
        shipment.wmsPoCode ? `PO ${shipment.wmsPoCode}` : null,
        shipment.wmsGrnCode
            ? `GRN ${shipment.wmsGrnCode}${shipment.wmsGrnStatus ? ` (${shipment.wmsGrnStatus.toLowerCase()})` : ''}`
            : null,
    ].filter(Boolean).join(' · ');
    const receiveWmsLabel = receiveStep
        ? receiveStep === 'COMPLETE_GRN'
            ? shipment.wmsGrnCode
                ? `Also complete GRN ${shipment.wmsGrnCode} in the WMS`
                : 'Also create and complete a GRN in the WMS'
            : shipment.wmsPoCode
                ? `Also create a GRN against WMS PO ${shipment.wmsPoCode}`
                : `Also ${WMS_STEP_LABEL[receiveStep]} (needs the shipment pushed first)`
        : null;
    const receiveWmsBlocked =
        (receiveStep === 'CREATE_GRN' && !shipment.wmsPoCode)
        || (receiveStep === 'COMPLETE_GRN' && !shipment.wmsGrnCode && orderNotPushed);
    const total = shipment.totalUnits ?? 0;
    const received = shipment.receivedUnits ?? 0;
    const rows: ItemRow[] = (shipment.items ?? []).map((i) => ({
        name: i.description || i.productId || i.materialId || '—',
        sku: i.productId || i.materialId || '—',
        quantity: i.quantity,
        received: i.receivedQuantity ?? 0,
    }));

    // GRD reached = goods are out of production even if not yet dispatched.
    const todayIso = new Date().toISOString().slice(0, 10);
    const grdReached =
        !!shipment.readinessDate &&
        shipment.readinessDate.slice(0, 10) <= todayIso;
    const milestones: TimelineRow[] = [
        { label: 'Goods Readiness Date', field: 'readinessDate', expected: shipment.expectedReadinessDate, plan: shipment.readinessDate, actual: grdReached ? shipment.readinessDate : null, reachedCounts: true },
        { label: 'Departure', field: 'departureDate', expected: shipment.expectedDepartureDate, plan: shipment.departureDate, actual: shipment.shippedAt, reachedCounts: false },
        { label: 'At Destination Port', field: 'arrivalDate', expected: shipment.expectedArrivalDate, plan: shipment.arrivalDate, actual: shipment.arrivedAt, reachedCounts: false },
        { label: 'Delivered', field: 'deliveryDate', expected: shipment.expectedDeliveryDate, plan: shipment.deliveryDate, actual: shipment.deliveredAt, reachedCounts: false },
    ];
    const hasExpected = milestones.some((m) => m.expected);
    const vendorLabel = shipment.vendorName || 'this vendor';
    const destinationLabel =
        (warehouses ?? []).find((w) => w.warehouseId === shipment.warehouseId)?.name ??
        shipment.warehouseId ??
        '';
    const actualSummary = shipment.deliveredAt
        ? `Delivered ${formatDateDisplay(shipment.deliveredAt)}`
        : shipment.arrivedAt
          ? 'At destination port'
          : shipment.shippedAt
            ? 'In transit'
            : grdReached
              ? 'Goods ready'
              : 'In production';

    return (
        <div
            className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/10 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="relative flex h-full w-full max-w-3xl flex-col gap-4 border-l border-slate-200 bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-3">
                            <h3 className="text-lg font-semibold text-slate-900 sm:text-xl">
                                {shipment.poNumber}
                            </h3>
                            <StatusBadge status={shipment.status} />
                        </div>
                        {shipment.motherOrderNumber ? (
                            <p className="text-sm text-slate-500">
                                Shipment of order {shipment.motherOrderNumber}
                            </p>
                        ) : null}
                        {manuallyReceived ? (
                            <p className="text-xs text-emerald-600">
                                Marked received manually
                                {shipment.manualReceivedAt
                                    ? ` on ${formatDateDisplay(shipment.manualReceivedAt)}`
                                    : ''}
                                {shipment.wmsGrnCode ? '' : ' · no GRN booked'}
                            </p>
                        ) : null}
                        {wmsSummary ? (
                            <p className="text-xs text-slate-500">WMS: {wmsSummary}</p>
                        ) : null}
                    </div>

                    <div className="flex items-center gap-2">
                        {canPushToWms ? (
                            <button
                                type="button"
                                onClick={pushToWms}
                                disabled={savingWmsPush || orderNotPushed}
                                title={pushHint}
                                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
                            >
                                {savingWmsPush ? 'Pushing…' : 'Push to WMS'}
                            </button>
                        ) : null}
                        {manuallyReceived ? (
                            // Reversing a receive exists only for mother-order shipments today.
                            isStandalone ? null : (
                                <button
                                    type="button"
                                    onClick={unmarkReceived}
                                    disabled={savingReceive}
                                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
                                >
                                    {savingReceive ? 'Reversing…' : 'Unmark received'}
                                </button>
                            )
                        ) : (
                            <button
                                type="button"
                                onClick={() => (receiveStep ? setConfirmReceive(true) : void markReceived(false))}
                                disabled={savingReceive || confirmReceive}
                                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {savingReceive ? 'Marking…' : 'Mark received'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                            aria-label="Close shipment details"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {confirmReceive && receiveWmsLabel ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-200 bg-emerald-50 px-6 py-3">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                checked={receivePushToWms && !receiveWmsBlocked}
                                disabled={receiveWmsBlocked}
                                onChange={(e) => setReceivePushToWms(e.currentTarget.checked)}
                            />
                            <span>
                                {receiveWmsLabel}
                                <span className="ml-1 text-xs text-slate-500">
                                    {receiveWmsBlocked && orderNotPushed
                                        ? '(push the order to the WMS first)'
                                        : '(external action)'}
                                </span>
                            </span>
                        </label>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmReceive(false)}
                                disabled={savingReceive}
                                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => void markReceived(receivePushToWms && !receiveWmsBlocked)}
                                disabled={savingReceive}
                                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {savingReceive ? 'Marking…' : 'Confirm received'}
                            </button>
                        </div>
                    </div>
                ) : null}

                <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
                    <SegmentedToggle
                        value={paneView}
                        onChange={(v) => setPaneView(v as 'timeline' | 'payments' | 'items')}
                        options={[
                            { label: 'Timeline', value: 'timeline' },
                            { label: 'Payments & Details', value: 'payments' },
                            { label: 'Items & SKUs', value: 'items' },
                        ]}
                    />
                </div>

                <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
                    {paneView === 'timeline' && (
                    <div>
                        {!isStandalone && <DispatchBlock shipment={shipment} onChanged={onChanged} />}
                        <div style={{ maxWidth: 340, marginBottom: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: textColors.secondary, marginBottom: 4 }}>
                                Destination warehouse
                            </div>
                            <Select
                                data={destinationOptions.map((w) => ({ value: w.warehouseId, label: w.name }))}
                                value={shipment.warehouseId ?? null}
                                onChange={setWarehouse}
                                placeholder={isStandalone ? 'Managed by the daily sync' : 'Set a destination to compute the timeline'}
                                searchable
                                clearable
                                disabled={savingWarehouse || isStandalone}
                            />
                            {noLanesYet && !isStandalone && (
                                <div style={{ marginTop: 4, fontSize: 11, color: textColors.muted }}>
                                    No lanes from {vendorLabel} yet — add one on the Lanes page
                                </div>
                            )}
                        </div>
                        <DataSheet<TimelineRow>
                            data={milestones}
                            rowHeight={56}
                            columns={[
                                {
                                    key: 'label',
                                    header: 'Milestone',
                                    width: 190,
                                    render: (label: string, r: TimelineRow) => {
                                        const v = variance(r.expected, r.actual ?? (r.reachedCounts ? r.plan : null));
                                        return (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                                <span style={{ width: 11, height: 11, borderRadius: '50%', flexShrink: 0, background: v.done ? v.color : 'transparent', border: `2px solid ${v.color}` }} />
                                                <span style={{ color: textColors.primary }}>{label}</span>
                                            </span>
                                        );
                                    },
                                },
                                {
                                    key: 'expected',
                                    header: 'Expected',
                                    width: 120,
                                    align: 'center',
                                    render: (v: string | null) => (
                                        <span style={{ color: textColors.secondary, fontVariantNumeric: 'tabular-nums' }}>
                                            {v ? formatDateDisplay(v) : '—'}
                                        </span>
                                    ),
                                },
                                {
                                    key: 'plan',
                                    header: 'Planned',
                                    width: 170,
                                    align: 'center',
                                    render: (_v: string | null, r: TimelineRow) =>
                                        isStandalone ? (
                                            <span style={{ fontSize: 13, color: textColors.primary, fontVariantNumeric: 'tabular-nums' }}>
                                                {r.plan ? formatDateDisplay(r.plan) : '—'}
                                            </span>
                                        ) : (
                                            <DateSinglePicker
                                                value={r.plan ?? ''}
                                                onChange={(iso) => {
                                                    // GRD is mandatory — it can be corrected but never cleared.
                                                    if (!iso && r.field === 'readinessDate') return;
                                                    setMilestone(r.field, iso || null);
                                                }}
                                                placeholder="Set date"
                                                clearable={r.field !== 'readinessDate'}
                                            />
                                        ),
                                },
                                {
                                    key: 'actual',
                                    header: 'Actual',
                                    width: 110,
                                    align: 'center',
                                    render: (_v: string | null, r: TimelineRow) => (
                                        <span
                                            style={{ fontSize: 13, color: r.actual ? textColors.primary : textColors.muted, fontVariantNumeric: 'tabular-nums' }}
                                            title={r.reachedCounts ? 'Counts once the date is reached' : r.actual ? 'Recorded' : 'Not yet marked'}
                                        >
                                            {r.actual ? formatDateDisplay(r.actual) : '—'}
                                        </span>
                                    ),
                                },
                                {
                                    key: 'variance',
                                    header: '',
                                    width: 130,
                                    align: 'right',
                                    render: (_v: unknown, r: TimelineRow) => {
                                        const v = variance(r.expected, r.actual ?? (r.reachedCounts ? r.plan : null));
                                        return v.text ? (
                                            <span style={{ fontSize: 11, fontWeight: 600, color: v.color, background: 'rgb(248, 250, 252)', padding: '2px 8px', borderRadius: 6 }}>
                                                {v.text}
                                            </span>
                                        ) : null;
                                    },
                                },
                            ]}
                            getRowKey={(r) => r.label}
                            bare
                            hideHeaderBar
                        />
                        {hasExpected ? (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgb(241, 245, 249)', fontSize: 13, color: textColors.secondary }}>
                                Expected delivery{' '}
                                <span style={{ color: textColors.primary, fontWeight: 600 }}>
                                    {shipment.expectedDeliveryDate ? formatDateDisplay(shipment.expectedDeliveryDate) : '—'}
                                </span>
                                {'  ·  '}
                                {actualSummary}
                            </div>
                        ) : (
                            <div style={{ marginTop: 8, fontSize: 12, color: textColors.muted }}>
                                {shipment.warehouseId
                                    ? // A destination without a lane behind it: nothing to derive the dates from.
                                      `No lane from ${vendorLabel} to ${destinationLabel}; expected dates need one`
                                    : 'Set a destination warehouse above to see the expected dates and variance.'}
                            </div>
                        )}
                    </div>
                    )}

                    {paneView === 'payments' && (
                        <>
                            <div>
                                <div style={panelSectionLabel}>Overview</div>
                                <div style={{ display: 'flex', gap: 40, marginBottom: 16 }}>
                                    <PanelField label="Vendor" value={shipment.vendorName || '—'} />
                                    <PanelField label="Parent Order" value={shipment.motherOrderNumber || '—'} />
                                    <PanelField
                                        label="Destination"
                                        value={
                                            (warehouses ?? []).find(
                                                (w) => w.warehouseId === shipment.warehouseId,
                                            )?.name ??
                                            shipment.warehouseId ??
                                            '—'
                                        }
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: 40 }}>
                                    <PanelField label="Shipment Qty" value={num(total)} />
                                    {cbmEnabled && (
                                        <PanelField
                                            label="CBM"
                                            value={
                                                shipment.totalCbm == null
                                                    ? '—'
                                                    : `${formatCount(shipment.totalCbm, { decimals: 2 })}${
                                                          shipment.maxCbmPerShipment ? ` of ${shipment.maxCbmPerShipment}` : ''
                                                      }`
                                            }
                                        />
                                    )}
                                    <PanelField label="Received" value={num(received)} />
                                    <PanelField label="Currency" value={shipment.currency || 'INR'} />
                                </div>
                            </div>

                            <ShipmentPaymentsBlock
                                motherOrderId={shipment.motherOrderId}
                                shipmentPoId={shipment.poNumber}
                                currency={shipment.currency || 'INR'}
                            />
                        </>
                    )}

                    {paneView === 'items' && (
                    <DataSheet<ItemRow>
                        data={rows}
                        columns={[
                            {
                                key: 'name',
                                header: 'SKU Name',
                                width: 220,
                                render: (v: string) => (
                                    <span style={{ fontWeight: 500, color: textColors.primary }}>{v}</span>
                                ),
                            },
                            {
                                key: 'sku',
                                header: 'SKU',
                                width: 160,
                                render: (v: string) => (
                                    <span style={{ color: textColors.secondary }}>{v}</span>
                                ),
                            },
                            {
                                key: 'quantity',
                                header: 'Ordered Quantity',
                                align: 'right',
                                width: 140,
                                render: (v: number) => (
                                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: textColors.primary }}>{num(v)}</span>
                                ),
                            },
                            {
                                key: 'received',
                                header: 'Received Quantity',
                                align: 'right',
                                width: 150,
                                render: (v: number) => (
                                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: (v ?? 0) > 0 ? textColors.primary : textColors.muted }}>{num(v ?? 0)}</span>
                                ),
                            },
                        ]}
                        title="Items"
                        getRowKey={(r) => `${r.sku}|${r.name}`}
                        emptyState="No items on this shipment."
                    />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShipmentDetailPanel;
